import React, { useEffect, useRef, useState } from "react";
import "./MyGlbViewer.css";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import homeicon from "../../assets/myGlbViwer/home.png"
import fronticon from "../../assets/myGlbViwer/iso_front.png"
import backicon from "../../assets/myGlbViwer/iso_back.png"
import topicon from "../../assets/myGlbViwer/iso_top.png"
import bottomicon from "../../assets/myGlbViwer/iso_bottom.png"
import righticon from "../../assets/myGlbViwer/iso_right.png"
import lefticon from "../../assets/myGlbViwer/iso_left.png"


const MyGlbViewer = ({ modelUrl , canvasHeight = 400, canvasWidth = 600, open }) => {
 
  const mountRef = useRef(null);
  const [isViewerReady, setViewerReady] = useState(false);
  const [currentView, setCurrentView] = useState('default');
  const [containerDimensions, setContainerDimensions] = useState({ width: 600, height: 400 });

  // These need to persist across renders, so define refs instead of let variables
  const sceneRef = useRef();
  const cameraRef = useRef();
  const modelRef = useRef();
  const controlsRef = useRef();
  const rendererRef = useRef();

  // Function to get container dimensions
  const getContainerDimensions = () => {
    if (mountRef.current) {
      const rect = mountRef.current.getBoundingClientRect();
      return { width: rect.width, height: rect.height };
    }
    return { width: 600, height: 400 };
  };

  // Update dimensions when container size changes
  useEffect(() => {
    const updateDimensions = () => {
      const dimensions = getContainerDimensions();
      setContainerDimensions(dimensions);
      
      if (rendererRef.current) {
        rendererRef.current.setSize(dimensions.width, dimensions.height);
        if (cameraRef.current) {
          cameraRef.current.aspect = dimensions.width / dimensions.height;
          cameraRef.current.updateProjectionMatrix();
        }
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Reset view to default every time modal is opened
  useEffect(() => {
    if (open) {
      setTimeout(() => setView('default'), 0);
    }
  }, [open]);

  useEffect(() => {
    let sceneWidth = containerDimensions.width;
    let sceneHeight = containerDimensions.height;
    if (modelUrl) {
      init(
        modelUrl,
        sceneWidth,
        sceneHeight,
        containerDimensions.width,
        containerDimensions.height
      );
    }

    // Clean up on unmount
    return () => {
      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (mountRef.current) {
          mountRef.current.innerHTML = "";
        }
      }
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
    };
  }, [modelUrl, containerDimensions.width, containerDimensions.height]);

  function init(blob, sceneWidth, sceneHeight, canvasWidth, canvasHeight) {
    setupScene();
    setupRenderer(canvasWidth, canvasHeight);
    setupLighting();
    setPerspectiveCamera(sceneWidth / sceneHeight); // Temporary perspective camera
    setupControls();
    loadModel(blob, sceneWidth, sceneHeight);
  }

  function setupScene() {
    const scene = new THREE.Scene();
    sceneRef.current = scene;
  }

  function setupRenderer(canvasWidth, canvasHeight) {
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(canvasWidth, canvasHeight);
    renderer.setClearColor(new THREE.Color(0xFFFFFF));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.VSMShadowMap;
    if (mountRef.current) {
      mountRef.current.innerHTML = "";
      mountRef.current.appendChild(renderer.domElement);
    }
    rendererRef.current = renderer;
  }

  function setupLighting() {
    const scene = sceneRef.current;
    if (!scene) return;

    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.9);
    directionalLight1.position.set(5, 10, 7.5);
    directionalLight1.castShadow = true;
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.9);
    directionalLight2.position.set(-5, -10, -7.5);
    directionalLight2.castShadow = true;
    scene.add(directionalLight2);
  }

  function setPerspectiveCamera(aspectRatio = window.innerWidth / window.innerHeight) {
    const camera = new THREE.PerspectiveCamera(25, aspectRatio, 0.001, 1000);
    camera.position.z = 1000;
    cameraRef.current = camera;
  }

  function setupControls() {
    if (!cameraRef.current || !rendererRef.current) return;
    const controls = new OrbitControls(cameraRef.current, rendererRef.current.domElement);
    controls.rotateSpeed = 2.5;
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controls.screenSpacePanning = true;
    controls.maxPolarAngle = Math.PI;
    controls.update();
    controlsRef.current = controls;
  }

  function loadModel(blob, sceneWidth, sceneHeight) {
    const url = blob;
    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        const scene = sceneRef.current;
        modelRef.current = gltf.scene;
        processModel(modelRef.current);
        scene.add(modelRef.current);

        // Set orthographic camera based on loaded model bounds
        setOrthographicCamera(modelRef.current, sceneWidth, sceneHeight);

        // Dispose and recreate controls with the new camera
        if (controlsRef.current) controlsRef.current.dispose();

        if (cameraRef.current && rendererRef.current) {
          const controls = new OrbitControls(cameraRef.current, rendererRef.current.domElement);
          controls.rotateSpeed = 2.5;
          controls.enableDamping = true;
          controls.dampingFactor = 0.25;
          controls.screenSpacePanning = true;
          controls.maxPolarAngle = Math.PI;
          controls.update();
          controlsRef.current = controls;

          setViewerReady(true); // Now viewer is fully ready
          setView("default");
          animate();
        } else {
          console.error("Camera or Renderer not initialized");
        }
      },
      undefined,
      (error) => {
        console.error("Error loading model:", error);
      }
    );
  }

  function processModel(model) {
    model.traverse((node) => {
      if (node.isMesh) {
        node.castShadow = false;
        node.receiveShadow = false;
        const edges = new THREE.EdgesGeometry(node.geometry, 50);
        const edgeLines = new THREE.LineSegments(
          edges,
          new THREE.LineBasicMaterial({
            color: 0xFFFFFF,
            transparent: true,
            opacity: 0.7,
          })
        );
        node.material.flatShading = false;
        node.material.needsUpdate = false;
        node.add(edgeLines);
      }
    });
  }

  function animate() {
    requestAnimationFrame(animate);
    if (controlsRef.current) controlsRef.current.update();
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  }

  function setOrthographicCamera(model, sceneWidth = window.innerWidth, sceneHeight = window.innerHeight) {
    if (!model) return;
    const width = sceneWidth;
    const height = sceneHeight;
    const aspect = width / height;

    model.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(model);

    if (box.isEmpty()) {
      console.error("Bounding box is empty! Model might not be loaded.");
      return;
    }

    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    modelRef.current.userData.center = center;
    const maxDim = Math.max(size.x, size.y, size.z);
    const frustumSize = maxDim * 1.5;

    const camera = new THREE.OrthographicCamera(
      (-frustumSize * aspect) / 2,
      (frustumSize * aspect) / 2,
      frustumSize / 2,
      -frustumSize / 2,
      0.1,
      1000
    );

    camera.position.set(center.x, center.y, maxDim * 2);
    camera.lookAt(center);
    camera.updateProjectionMatrix();

    cameraRef.current = camera;
  }

  function setView(view) {
    if (!cameraRef.current || !controlsRef.current) return;

    // Fixed positions with consistent distances
    const positions = {
      front: [0, 0, 1],
      back: [0, 0, -1],
      top: [0, 1, 0],      // Fixed: consistent distance
      bottom: [0, -1, 0],  // Fixed: consistent distance
      right: [1, 0, 0],
      left: [-1, 0, 0],
      isometric: [1, 1, 1],
      default: [1, 1, 1],
    };

    // Check if we need intermediate waypoint for problematic transitions
    const needsWaypoint = (currentView === 'top' && view === 'bottom') ||
      (currentView === 'bottom' && view === 'top');

    const isDefault = view === 'default';
    const zoom = isDefault ? 1.1 : undefined;

    if (needsWaypoint) {
      // First transition to isometric view
      setCameraPositionAndZoom([1, 1, 1], zoom, 350);
      // Then transition to target view after a delay
      setTimeout(() => {
        const position = positions[view] || positions["isometric"];
        setCameraPositionAndZoom(position, zoom, 350);
        setCurrentView(view);
      }, 350);
    } else {
      const position = positions[view] || positions["isometric"];
      setCameraPositionAndZoom(position, zoom);
      setCurrentView(view);
    }

    if (modelRef.current && modelRef.current.userData.center) {
      controlsRef.current.target.copy(modelRef.current.userData.center);
    } else {
      controlsRef.current.target.set(0, 0, 0);
    }
    controlsRef.current.update();
  }

  // Improved animation function with spherical interpolation
  function animateCameraToSpherical(targetPos, targetLookAt, duration = 1300) {
    if (!cameraRef.current || !controlsRef.current) return;

    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const startPos = camera.position.clone();
    const startTarget = controls.target.clone();
    const endPos = targetPos.clone();
    const endTarget = targetLookAt.clone();

    // Convert positions to spherical coordinates relative to the target
    const startRelative = startPos.clone().sub(startTarget);
    const endRelative = endPos.clone().sub(endTarget);

    const startSpherical = new THREE.Spherical().setFromVector3(startRelative);
    const endSpherical = new THREE.Spherical().setFromVector3(endRelative);

    // Handle angle wrapping for smoother interpolation
    if (Math.abs(endSpherical.theta - startSpherical.theta) > Math.PI) {
      if (endSpherical.theta > startSpherical.theta) {
        startSpherical.theta += 2 * Math.PI;
      } else {
        endSpherical.theta += 2 * Math.PI;
      }
    }

    const startTime = performance.now();

    function animate() {
      const now = performance.now();
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);

      // Smooth easing function (ease-out cubic)
      const eased = 1 - Math.pow(1 - t, 3);

      // Interpolate spherical coordinates
      const currentSpherical = new THREE.Spherical(
        THREE.MathUtils.lerp(startSpherical.radius, endSpherical.radius, eased),
        THREE.MathUtils.lerp(startSpherical.phi, endSpherical.phi, eased),
        THREE.MathUtils.lerp(startSpherical.theta, endSpherical.theta, eased)
      );

      // Convert back to world coordinates
      const currentRelative = new THREE.Vector3().setFromSpherical(currentSpherical);
      const currentTarget = new THREE.Vector3().lerpVectors(startTarget, endTarget, eased);
      camera.position.copy(currentRelative.add(currentTarget));
      controls.target.copy(currentTarget);

      camera.updateProjectionMatrix();
      controls.update();

      if (t < 1) {
        requestAnimationFrame(animate);
      }
    }
    animate();
  }

  function setCameraPositionAndZoom(direction, zoomFactor, duration = 1300) {
    if (!cameraRef.current || !modelRef.current) return;

    const center = modelRef.current.userData.center || new THREE.Vector3(0, 0, 0);
    const dir = new THREE.Vector3(...direction).normalize();
    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = maxDim * 1.5;
    const cameraPos = dir.multiplyScalar(distance).add(center);

    // Use improved spherical animation
    animateCameraToSpherical(cameraPos, center, duration);

    if (cameraRef.current.isOrthographicCamera && zoomFactor !== undefined) {
      cameraRef.current.zoom = zoomFactor;
      cameraRef.current.updateProjectionMatrix();
    }
  }

  return (

    
    <div className="glbviewer">
      <div className="glbviewer-canvas-area" >
        <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
        {open && (
          <div className="views-overlay" style={{ position: 'absolute', top: 10, left: 10, zIndex: 10, display: 'flex', gap: 4, pointerEvents: 'auto', padding: 5 }}>
            <button className="button" onClick={() => setView("front")} disabled={!isViewerReady}>
              <img src={fronticon} alt="Front" style={{ width: 32, height: 32 }} />
              <span className="custom-tooltip">Front</span>
            </button>
            <button className="button" onClick={() => setView("back")} disabled={!isViewerReady}>
              <img src={backicon} alt="Back" style={{ width: 32, height: 32 }} />
              <span className="custom-tooltip">Back</span>
            </button>
            <button className="button" onClick={() => setView("top")} disabled={!isViewerReady}>
              <img src={topicon} alt="Top" style={{ width: 32, height: 32 }} />
              <span className="custom-tooltip">Top</span>
            </button>
            <button className="button" onClick={() => setView("bottom")} disabled={!isViewerReady}>
              <img src={bottomicon} alt="Bottom" style={{ width: 32, height: 32 }} />
              <span className="custom-tooltip">Bottom</span>
            </button>
            <button className="button" onClick={() => setView("right")} disabled={!isViewerReady}>
              <img src={righticon} alt="Right" style={{ width: 32, height: 32 }} />
              <span className="custom-tooltip">Right</span>
            </button>
            <button className="button" onClick={() => setView("left")} disabled={!isViewerReady}>
              <img src={lefticon} alt="Left" style={{ width: 32, height: 32 }} />
              <span className="custom-tooltip">Left</span>
            </button>
            <button className="button" onClick={() => setView("default")} disabled={!isViewerReady}>
              <span className="custom-tooltip" >Default</span>
              <img src={homeicon} alt="Default" style={{ width: 32, height: 32 }} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
  
};

export default MyGlbViewer;
